export interface CustomerServiceOptions {
  baseUrl: string;
  tenantId: string;
  appId: string;
  channelId: string;
  userId: string;
  userName: string;
  avatarUrl?: string;
}

interface KefuChatSDK {
  init: (options: Record<string, unknown>) => HTMLIFrameElement;
  open: () => boolean;
  close: () => boolean;
  destroy: () => void;
}

declare global {
  interface Window {
    KefuChat?: KefuChatSDK;
  }
}

const sdkScriptID = 'mechat-customer-service-sdk';
const sdkVersion = '20260904-text-link-v3';
const embeddedPageVersion = '20260904-text-link-v3';
let sdkLoadPromise: Promise<KefuChatSDK> | null = null;
let activeConfigurationKey = '';

function normalizedBaseURL(value: string): string {
  return value.trim().replace(/\/+$/, '');
}

function loadCustomerServiceSDK(baseUrl: string): Promise<KefuChatSDK> {
  if (window.KefuChat) return Promise.resolve(window.KefuChat);
  if (sdkLoadPromise) return sdkLoadPromise;

  sdkLoadPromise = new Promise<KefuChatSDK>((resolve, reject) => {
    const existing = document.getElementById(sdkScriptID) as HTMLScriptElement | null;
    const script = existing || document.createElement('script');
    const handleLoad = () => window.KefuChat ? resolve(window.KefuChat) : reject(new Error('客服组件加载失败'));
    const handleError = () => reject(new Error('客服组件加载失败'));

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
    if (!existing) {
      script.id = sdkScriptID;
      script.src = `${normalizedBaseURL(baseUrl)}/kefu-sdk.js?v=${sdkVersion}`;
      script.async = true;
      document.head.appendChild(script);
    }
  }).catch((error) => {
    sdkLoadPromise = null;
    document.getElementById(sdkScriptID)?.remove();
    throw error;
  });

  return sdkLoadPromise;
}

export async function openCustomerService(options: CustomerServiceOptions): Promise<void> {
  const baseUrl = normalizedBaseURL(options.baseUrl);
  const sdk = await loadCustomerServiceSDK(baseUrl);
  if (typeof sdk.open !== 'function') {
    throw new Error('客服组件版本过旧，请刷新页面后重试');
  }

  const configurationKey = JSON.stringify({
    baseUrl,
    tenantId: options.tenantId,
    appId: options.appId,
    channelId: options.channelId,
    userId: options.userId,
  });

  if (activeConfigurationKey !== configurationKey || !document.getElementById('kefu-chat-iframe')) {
    sdk.init({
      baseUrl,
      path: `/?embedVersion=${embeddedPageVersion}#/chat-demo`,
      tenantId: options.tenantId,
      appId: options.appId,
      channelId: options.channelId,
      userId: options.userId,
      userName: options.userName,
      avatarUrl: options.avatarUrl || '',
      siteDomain: window.location.hostname,
      siteUrl: window.location.href,
      showLauncher: false,
      defaultOpen: true,
      mobileCollapsedWidth: 36,
      mobileCollapsedHeight: 36,
      mobileEdge: 12,
    });
    activeConfigurationKey = configurationKey;
  }

  sdk.open();
}

export function destroyCustomerService(): void {
  window.KefuChat?.destroy();
  activeConfigurationKey = '';
}
